import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialog } from '@angular/material/dialog';
import { OperativeWeekConfig } from 'src/app/shared/interfaces/holidays.interface';
import { HolidaysService } from 'src/app/shared/services/holidays/holidays.service';
import { DriversNotAvailableDialogComponent } from '../drivers-not-available-dialog/drivers-not-available-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ExcelService } from 'src/app/shared/services/excel/excel.service';

@Component({
  selector: 'app-horizontal-calendar',
  imports: [
    MatIconModule,
    MatIconButton,
    FormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatTooltipModule,
  ],
  templateUrl: './horizontal-calendar.component.html',
  styleUrl: './horizontal-calendar.component.css',
})
export class HorizontalCalendarComponent implements OnChanges {
  @Input() weeks: OperativeWeekConfig[] = [];
  @Input() isLoading: boolean = false;
  @Input() refreshingWeekNumber: number | null = null;
  @Input() operativaGroupId?: number;
  @Input() exerciseId?: number;
  @Input() year?: number;
  @Input() operativaGroupName?: string;
  @Output() refreshWeek = new EventEmitter<number>();
  @Output() weekUpdated = new EventEmitter<void>();
  @Output() exportLoading = new EventEmitter<boolean>();

  editingWeekNumber: number | null = null;

  // Array representing N dynamic right columns
  rightColumns: Array<{
    id: number;
    danger?: boolean;
    week: string;
    weekNumber: number;
    dates: string;
    metrics: (number | string)[];
  }> = [];

  constructor(
    private holidaysService: HolidaysService,
    private dialog: MatDialog,
    private excelService: ExcelService<any>,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['weeks'] && this.weeks) {
      this.transformWeeksToColumns();
    }
  }

  private transformWeeksToColumns(): void {
    this.rightColumns = this.weeks.map((week) => {
      // Determine if week has warnings (negative values in critical fields)
      const hasDanger =
        week.camions_disponibles < 0 || week.conductors_disponibles < 0;

      return {
        id: week.id,
        danger: hasDanger,
        week: `Semana ${week.week_number}`,
        weekNumber: week.week_number,
        dates: `${week.start_date} - ${week.end_date}`,
        metrics: [
          // CAMIONES section
          week.camions_assignats, // 0 - CAMIONES TOTAL
          week.camions_plaza, // 1 - CAMIONES PLAZA (editable)
          week.camions_averiats, // 2 - CAMIONES AVERIADOS
          week.camions_parats_objectiu, // 3 - OBJETIVO CAMIONES PARADOS (editable)
          week.camions_futur, // 4 - CAMIONES A FUTURO (editable)
          week.camions_disponibles, // 5 - CAMIONES DISPONIBLES
          week.camions_desitjats_circulacio, // 6 - CAMIONES DESEADOS CIRCULACIÓN
          week.camions_parats_real, // 7 - CAMIONES PARADOS

          // CONDUCTORES section
          week.conductors_total, // 8 - CONDUCTORES TOTAL
          week.conductors_vacances, // 9 - CONDUCT. VACACIONES
          week.conductors_indisponibles + week.doble_tripulacio, // 10 - CONDUCT. INDISPONIBLES (includes doble_tripulacio)
          week.camions_disponibles - week.conductors_disponibles - week.camions_parats_objectiu, // 11 - CONDUCT. INCORPORAR
          week.conductors_previstos, // 12 - CONDUCT. PREVISIÓN (editable)
          week.conductors_disponibles, // 13 - CONDUCT. DISPONIBLES

          // SLOTS section
          week.slots, // 14 - SLOTS (editable)
          week.slots_requests_accepted, // 15 - SLOTS ACEPTADOS
          week.slots_requests_pending, // 16 - SLOTS PENDIENTES
        ],
      };
    });
  }

  onRefreshClick(weekNumber: number): void {
    this.refreshWeek.emit(weekNumber);
  }

  editFields(weekNumber: number): void {
    // Toggle edit mode for this specific week
    if (this.editingWeekNumber === weekNumber) {
      // Save changes when closing edit mode
      this.saveWeekConfig(weekNumber);
    } else {
      // Start editing this week
      this.editingWeekNumber = weekNumber;
    }
  }

  cancelFieldsEdit(): void {
    this.editingWeekNumber = null;
  }

  saveWeekConfig(weekNumber: number): void {
    const column = this.rightColumns.find(
      (col) => col.weekNumber === weekNumber,
    );
    if (!column) {
      console.error('Column not found for week number:', weekNumber);
      return;
    }

    // Get the edited values from the metrics array
    // Editable fields: camions_plaza (1), camions_parats_objectiu (3), camions_futur (4), conductors_previstos (11), slots (13)
    const camions_plaza = Number(column.metrics[1]);
    const camions_parats_objectiu = Number(column.metrics[3]);
    const camions_futur = Number(column.metrics[4]);
    const conductors_previstos = Number(column.metrics[12]);
    const slots = Number(column.metrics[14]);

    const updateData = {
      camions_plaza,
      camions_parats_objectiu,
      camions_futur,
      conductors_previstos,
      slots,
    };

    this.holidaysService.updateWeekConfig(column.id, updateData).subscribe({
      next: (response) => {
        // console.log('Week config updated successfully:', response);
        this.editingWeekNumber = null;
        // Emit event to parent to refresh data
        this.weekUpdated.emit();
      },
      error: (error) => {
        console.error('Error updating week config:', error);
        // Optionally keep edit mode open on error
        // this.editingWeekNumber = weekNumber;
      },
    });
  }

  isEditingWeek(weekNumber: number): boolean {
    return this.editingWeekNumber === weekNumber;
  }

  isRefreshingWeek(weekNumber: number): boolean {
    return this.refreshingWeekNumber === weekNumber;
  }

  onInfoClick(weekNumber: number, type: 'vacation' | 'unavailable'): void {
    if (!this.operativaGroupId || !this.exerciseId) {
      console.error('Missing operativaGroupId or exerciseId');
      return;
    }

    // Get doble_tripulacio from the week data
    const week = this.weeks.find((w) => w.week_number === weekNumber);
    const doble_tripulacio = week?.doble_tripulacio ?? 0;

    // Open dialog immediately with loading state
    const dialogRef = this.dialog.open(DriversNotAvailableDialogComponent, {
      width: '700px',
      maxHeight: '90vh',
      data: { weekNumber, type, doble_tripulacio },
    });

    // Fetch data and update dialog
    const requestData = {
      operativa_group_id: this.operativaGroupId,
      exercise_id: this.exerciseId,
      week_numbers: [weekNumber],
    };

    this.holidaysService.getWeekDriverDetails(requestData).subscribe({
      next: (response) => {
        if (response.weeks && response.weeks.length > 0) {
          const weekData = response.weeks[0];
          dialogRef.componentInstance.setWeekData(weekData);
        }
      },
      error: (error) => {
        console.error('Error fetching week driver details:', error);
        dialogRef.close();
      },
    });
  }

  exportWeekToExcel(weekNumber: number): void {
    const week = this.weeks.find((w) => w.week_number === weekNumber);
    if (!week) {
      console.error('Week not found:', weekNumber);
      return;
    }

    if (!this.operativaGroupId || !this.exerciseId) {
      console.error('Missing operativaGroupId or exerciseId');
      return;
    }

    // Fetch download data from API
    const requestData = {
      operativa_group_id: this.operativaGroupId,
      exercise_id: this.exerciseId,
      week_numbers: [weekNumber],
    };

    this.exportLoading.emit(true);
    this.holidaysService.downloadOperativaAssignments(requestData).subscribe({
      next: (response) => {
        this.exportLoading.emit(false);
        const yearStr = this.year ?? week.year;
        const groupName = this.operativaGroupName ?? 'Operativa';

        // Vertical format: each row contains a field name and its value
        const dataToExport: Record<string, string | number>[] = [
          { Campo: 'AÑO', Valor: yearStr },
          { Campo: 'OPERATIVA', Valor: groupName },
          { Campo: 'SEMANA', Valor: weekNumber },
          { Campo: 'PERÍODO', Valor: `${week.start_date} - ${week.end_date}` },
          { Campo: 'CAMIONES TOTAL', Valor: week.camions_assignats },
          { Campo: 'CAMIONES PLAZA', Valor: week.camions_plaza },
          { Campo: 'CAMIONES AVERIADOS', Valor: week.camions_averiats },
          {
            Campo: 'OBJETIVO CAMIONES PARADOS',
            Valor: week.camions_parats_objectiu,
          },
          { Campo: 'CAMIONES A FUTURO', Valor: week.camions_futur },
          { Campo: 'CAMIONES DISPONIBLES', Valor: week.camions_disponibles },
          {
            Campo: 'CAMIONES DESEADOS CIRCULACIÓN',
            Valor: week.camions_desitjats_circulacio,
          },
          { Campo: 'CAMIONES PARADOS', Valor: week.camions_parats_real },
          { Campo: 'CONDUCTORES TOTAL', Valor: week.conductors_total },
          { Campo: 'CONDUCT. VACACIONES', Valor: week.conductors_vacances },
          {
            Campo: 'CONDUCT. INDISPONIBLES',
            Valor: week.conductors_indisponibles + week.doble_tripulacio,
          },
          {
            Campo: 'CONDUCT. INCORPORAR',
            Valor: week.camions_disponibles - week.conductors_disponibles - week.camions_parats_objectiu,
          },
          { Campo: 'CONDUCT. PREVISIÓN', Valor: week.conductors_previstos },
          { Campo: 'CONDUCT. DISPONIBLES', Valor: week.conductors_disponibles },
          { Campo: 'SLOTS', Valor: week.slots },
          { Campo: 'SLOTS ACEPTADOS', Valor: week.slots_requests_accepted },
          { Campo: 'SLOTS PENDIENTES', Valor: week.slots_requests_pending },
        ];

        // Add two blank rows
        dataToExport.push({
          Campo: '',
          Valor: '',
          Estado: '',
        });
        dataToExport.push({
          Campo: '',
          Valor: '',
          Estado: '',
        });

        // Add driver data rows (reusing Campo/Valor columns for Name/Plates)
        for (const driver of response.drivers) {
          const plates =
            driver.plates?.length > 0 ? driver.plates.join(', ') : '';
          const vacation = driver.vacation
            ? `Vacaciones: ${driver.vacation.initDate.split('T')[0]} - ${driver.vacation.endDate.split('T')[0]}`
            : '';
          const unavailable = driver.unavailable
            ? `${driver.unavailable.typeLabel}: ${driver.unavailable.initDate.split('T')[0]} - ${driver.unavailable.endDate.split('T')[0]}`
            : '';

          // Combine vacation and unavailable into a single Estado field
          const estado = [vacation, unavailable].filter(Boolean).join(' | ');

          dataToExport.push({
            Campo: driver.name,
            Valor: plates,
            Estado: estado,
          });
        }

        const fileName = `${yearStr} ${groupName} - Semana ${weekNumber}`;
        this.excelService.exportExcel(dataToExport, fileName, true);
      },
      error: (error) => {
        this.exportLoading.emit(false);
        console.error('Error fetching download data:', error);
      },
    });
  }
}
